import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { Navbar } from '../layout/Navbar';
import { Footer } from './Footer/Footer';
import { ThemeProvider } from '../../../core/providers/ThemeProvider';
import { getUserTypeFromPath } from '../../utils/userTypeUtils';
import { useUserStore } from '../../../app/store/useUserStore';
import { isSenderRole, isTravellerRole } from '../../utils/roleUtils';
import type { LayoutProps } from './types';
import { NavigationBar } from './NavigationBar';
import { createMuiTheme } from '../../styles/theme/theme';
import { FloatingActionButton } from '../ui/FloatingActionButton/FloatingActionButton';

interface MainLayoutExtendedProps extends LayoutProps {
  showFAB?: boolean;
}

export const MainLayout: React.FC<MainLayoutExtendedProps> = ({
  children,
  showHeader = true,
  showFooter = true,
  showFAB = false
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { user } = useUserStore();

  // For authenticated users, use their actual role. Otherwise derive from URL
  const userType = (isAuthenticated && user?.user_role)
    ? user.user_role
    : (getUserTypeFromPath(location.pathname) || 'traveller');
  // Conditionally render AuthNavbar or Navbar based on authentication
  const NavbarComponent = isAuthenticated ? <NavigationBar /> : <Navbar />;

  // Determine if FAB should be shown - for both sender and traveller
  const shouldShowFAB =
    showFAB &&
    isAuthenticated &&
    (isSenderRole(user?.user_role) || isTravellerRole(user?.user_role));

  // Create MUI theme based on user role
  const muiTheme = useMemo(() => createMuiTheme(userType), [userType]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <ThemeProvider userType={userType}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f9fafb',
          }}
        >
          {showHeader && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                display: 'flex',
                justifyContent: 'center',
                px: { xs: 2, sm: 3, lg: 4 },
                py: 2,
                pb: { xs: 2.5, sm: 2.5, lg: 3 },
                pointerEvents: 'none',
                '@media (max-width: 320px) and (max-height: 300px)': {
                  position: 'static',
                  px: 1,
                  py: 0.5,
                  pb: 0.5,
                },
              }}
            >
              <Box sx={{ pointerEvents: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
                {NavbarComponent}
              </Box>
            </Box>
          )}

          <Box
            component="main"
            sx={{
              flex: 1,
              mt: showHeader ? { xs: '100px', sm: '110px', lg: '120px' } : 0,
              '@media (max-width: 320px) and (max-height: 300px)': {
                mt: 0,
              },
            }}
          >
            {children}
          </Box>

          {showFooter && <Footer userType={userType} />}

          {/* Floating Action Button */}
          {shouldShowFAB && <FloatingActionButton />}
        </Box>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

MainLayout.displayName = 'MainLayout';